import {FilterSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/filter_section.types';
import {useQuantityFormat, useTranslation} from '@aboutyou/i18n';
import {
  useTrackFeature,
  useTrackFilter,
  useViewOptionsTracking,
} from '@aboutyou/tracking';
import {
  ErrorBoundary,
  FilterFlyoutFooter,
  FilterFlyoutSections,
  FilterFlyoutSort,
  FilterFlyoutViewOptions,
  ProductImageType,
} from '@aboutyou/ui-components';
import {usePrevious} from '@aboutyou/utils';
import React, {useEffect, useState} from 'react';
import {useQuickFilter} from '../../../backend-services/CategoryStream/utils';
import {usePotentialFilters, useSorting} from '../../ProductStream/utils';
import {useOpenFilterFlyout} from '../../ProductStream/utils/useOpenFilterFlyout';
import {SetFilterObject} from '../../ProductStream/utils/usePotentialFilters';
import {normalizeProductImageType} from '../shared/normalizeProductImageType';
import {FilterFlyoutProps} from './FilterFlyout';
import {FilterSectionAppliedFilters} from './FilterSections/FilterSectionAppliedFilters';
import {FilterSectionAttribute} from './FilterSections/FilterSectionAttribute';
import {FilterSectionBrand} from './FilterSections/FilterSectionBrand';
import {FilterSectionColor} from './FilterSections/FilterSectionColor';
import {FilterSectionDiscount} from './FilterSections/FilterSectionDiscount';
import {FilterSectionGroupedAttributes} from './FilterSections/FilterSectionGroupedAttributes';
import {FilterSectionMaterial} from './FilterSections/FilterSectionMaterial';
import {FilterSectionPrice} from './FilterSections/FilterSectionPrice';
import {FilterSectionSizeGrouped} from './FilterSections/FilterSectionSizeGrouped';
import {FilterSectionSizeSimple} from './FilterSections/FilterSectionSizeSimple';
import {FilterSectionYourDeals} from './FilterSections/FilterSectionYourDeals';
import {useLocalizedSortingOptions} from './helpers';

interface FlyoutFiltersProps
  extends Omit<FilterFlyoutProps, 'FilterContent' | 'FilterFooter'> {
  availableFilters: Array<FilterSection>;
  onClose: () => void;
  onPrimaryButtonClick: () => void;
  productCount: number;
  enableWhiteBackground?: boolean;
  appliedFiltersCount: number;
  submitButtonLabel?: string;
}

type ModifiedSetFilterObject = Omit<
  SetFilterObject,
  'label' | 'shouldTrackImmediately'
>;

export const FlyoutFilters: React.FC<FlyoutFiltersProps> = ({
  availableFilters,
  gridType,
  onChangeGridType,
  onChangeProductImageType,
  productImageType: userProductImageType,
  onClose,
  productCount,
  onPrimaryButtonClick,
  appliedFiltersCount,
  submitButtonLabel,
}) => {
  const {
    filters: activeFilters,
    resetFilter,
    resetFilters,
    setFilter,
    setFilters,
    submitFiltersInBulk,
    addFilterForBulkTracking,
  } = usePotentialFilters();
  const {
    openedFlyoutSlug,
    hideFilterFlyout,
    openFilterFlyout,
    submitFilterFlyout,
  } = useOpenFilterFlyout();
  const {favouriteBrands} = useQuickFilter();
  const {sorting, setSorting} = useSorting();
  const {trackOnSelect: trackOnSelectNewProdImgType} = useViewOptionsTracking();
  const sortingOptions = useLocalizedSortingOptions();
  const {t} = useTranslation();
  const quantityFormat = useQuantityFormat();
  const previousAppliedFiltersCount = usePrevious(appliedFiltersCount);
  const [appliedFilterCountOnMount, setAppliedFilterCountOnMount] =
    useState(appliedFiltersCount);

  const showProductCount = quantityFormat(productCount);

  const trackFeature = useTrackFeature();
  const trackFilter = useTrackFilter();

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setAppliedFilterCountOnMount(appliedFiltersCount);
  }, []);

  const handleOnChangeProductImgType = (productImgType: ProductImageType) => {
    onChangeProductImageType(productImgType);

    trackOnSelectNewProdImgType(
      productImgType.toString(),
      addFilterForBulkTracking,
    );
  };

  const onSort = (optionId: string) => {
    setSorting(optionId, {
      shouldTrackImmediately: false,
      addFilterForBulkTracking,
      shouldScrollToTop: true,
    });
  };

  const onSetFilter = ({
    filterId,
    filterSection,
    filterSlug,
    singleSelectOnly,
    trackingParams,
  }: ModifiedSetFilterObject) => {
    setFilter({
      filterId,
      filterSection,
      filterSlug,
      singleSelectOnly,
      label: 'filter_modal',
      shouldTrackImmediately: false,
      trackingParams,
    });
  };

  const handlePrimaryButtonClick = () => {
    submitFiltersInBulk();
    onPrimaryButtonClick();
    trackFeature({eventType: 'click', label: 'filterApplied'});
    if (favouriteBrands) {
      const brandFilter = availableFilters.find(
        item => item.type?.$case === 'brand',
      );

      if (brandFilter && brandFilter.type?.$case === 'brand') {
        trackFilter(
          brandFilter.type.brand.slug,
          favouriteBrands,
          'attribute',
          'submit',
          'quick_filters',
        );
      }
    }
  };

  const defaultSubmitButtonLabel = t(`Show {showProductCount} items`, {
    showProductCount,
  });

  return (
    <>
      <FilterFlyoutSections>
        {availableFilters.map((filter: FilterSection) => {
          if (!filter?.type?.$case) return null;

          switch (filter.type.$case) {
            case 'appliedFilter': {
              const appliedFiltersData = filter.type.appliedFilter;

              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key="filter-flyout-section-attribute-appliedFilter"
                  renderContext="category_filter_section_attribute"
                >
                  <FilterSectionAppliedFilters
                    data={appliedFiltersData}
                    enableFlyoutFilter
                  />
                </ErrorBoundary>
              );
            }

            case 'attribute': {
              const attributeFilterData = filter.type.attribute;

              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-flyout-section-attribute-${attributeFilterData.slug}`}
                  renderContext="category_filter_section_attribute"
                >
                  <FilterSectionAttribute
                    activeFilters={activeFilters}
                    data={attributeFilterData}
                    hide={hideFilterFlyout}
                    isOpen={openedFlyoutSlug === attributeFilterData.slug}
                    open={openFilterFlyout(attributeFilterData.slug)}
                    submit={submitFilterFlyout}
                    setFilter={onSetFilter}
                    isSingleSelect={attributeFilterData.isSingleSelect}
                    enableFlyoutFilter={true}
                  />
                </ErrorBoundary>
              );
            }

            case 'brand': {
              const brandFilterData = filter.type.brand;

              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-flyout-section-brand-${brandFilterData.slug}`}
                  renderContext="category_filter_section_brand"
                >
                  <FilterSectionBrand
                    activeFilters={activeFilters}
                    data={brandFilterData}
                    submit={submitFilterFlyout}
                    hide={hideFilterFlyout}
                    isOpen={openedFlyoutSlug === brandFilterData.slug}
                    open={openFilterFlyout(brandFilterData.slug)}
                    setFilter={onSetFilter}
                  />
                </ErrorBoundary>
              );
            }

            case 'color':
              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-flyout-section-color`}
                  renderContext="category_filter_section_color"
                >
                  <FilterSectionColor
                    activeFilters={activeFilters}
                    data={filter.type.color}
                    setFilter={onSetFilter}
                    hide={hideFilterFlyout}
                    isOpen={openedFlyoutSlug === 'color'}
                    open={openFilterFlyout('color')}
                    submit={submitFilterFlyout}
                    enableFlyoutFilter={true}
                  />
                </ErrorBoundary>
              );

            case 'material': {
              const materialFilterData = filter.type.material;
              const isOpen =
                materialFilterData.materialFilter?.slug !== undefined &&
                openedFlyoutSlug === materialFilterData.materialFilter.slug;

              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-flyout-section-material`}
                  renderContext="category_filter_section_material"
                >
                  <FilterSectionMaterial
                    activeFilters={activeFilters}
                    data={materialFilterData}
                    hide={hideFilterFlyout}
                    isOpen={isOpen}
                    open={openFilterFlyout(
                      materialFilterData.materialFilter.slug,
                    )}
                    submit={submitFilterFlyout}
                    resetFilter={resetFilter}
                    setFilter={onSetFilter}
                    enableFlyoutFilter={true}
                  />
                </ErrorBoundary>
              );
            }

            case 'groupedSize':
              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-flyout-section-groupedSize`}
                  renderContext="category_filter_section_groupedSize"
                >
                  <FilterSectionSizeGrouped
                    activeFilters={activeFilters}
                    data={filter.type.groupedSize}
                    hide={hideFilterFlyout}
                    isOpen={openedFlyoutSlug === 'groupedSize'}
                    open={openFilterFlyout('groupedSize')}
                    submit={submitFilterFlyout}
                    resetFilter={resetFilter}
                    resetFilters={resetFilters}
                    setFilter={onSetFilter}
                    enableFlyoutFilter={true}
                  />
                </ErrorBoundary>
              );

            case 'discountLevel':
              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-flyout-section-discount-level`}
                  renderContext="category_filter_section_discount_level"
                >
                  <FilterSectionDiscount
                    activeFilters={activeFilters}
                    data={filter.type.discountLevel}
                    resetFilter={resetFilter}
                    setFilter={onSetFilter}
                    hide={hideFilterFlyout}
                    isOpen={openedFlyoutSlug === 'discountLevel'}
                    open={openFilterFlyout('discountLevel')}
                    submit={submitFilterFlyout}
                  />
                </ErrorBoundary>
              );

            case 'price':
              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-flyout-section-price`}
                  renderContext="category_filter_section_price"
                >
                  <FilterSectionPrice
                    activeFilters={activeFilters}
                    data={filter.type.price}
                    resetFilter={resetFilter}
                    setFilter={onSetFilter}
                    hide={hideFilterFlyout}
                    isOpen={openedFlyoutSlug === 'price'}
                    open={openFilterFlyout('price')}
                    submit={submitFilterFlyout}
                  />
                </ErrorBoundary>
              );

            case 'simpleSize':
              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-flyout-section-simpleSize`}
                  renderContext="category_filter_section_simpleSize"
                >
                  <FilterSectionSizeSimple
                    activeFilters={activeFilters}
                    data={filter.type.simpleSize}
                    setFilter={onSetFilter}
                    enableFlyoutFilter={true}
                    hide={hideFilterFlyout}
                    isOpen={openedFlyoutSlug === 'simpleSize'}
                    open={openFilterFlyout('simpleSize')}
                    submit={submitFilterFlyout}
                  />
                </ErrorBoundary>
              );

            case 'sortOptions':
              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-flyout-section-sort`}
                  renderContext="category_filter_section_sort_options"
                >
                  <FilterFlyoutSort
                    activeOptionId={sorting ?? 'topseller'}
                    onSelect={onSort}
                    options={sortingOptions}
                    hide={hideFilterFlyout}
                    isOpen={openedFlyoutSlug === 'sort'}
                    open={openFilterFlyout('sort')}
                    submit={submitFilterFlyout}
                    enableFlyoutFilter={true}
                    showAsRadioGroup={true}
                  />
                </ErrorBoundary>
              );

            case 'viewOptions': {
              const viewOptionsData = filter.type.viewOptions;
              const defaultProductImageType =
                normalizeProductImageType(viewOptionsData);

              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-flyout-section-view`}
                  renderContext="category_filter_section_view_options"
                >
                  <FilterFlyoutViewOptions
                    gridType={gridType}
                    onSetGridType={onChangeGridType}
                    onSetProductImageType={handleOnChangeProductImgType}
                    productImageType={
                      userProductImageType ?? defaultProductImageType
                    }
                    enableFlyoutFilter={true}
                    withBottomMargin={false}
                  />
                </ErrorBoundary>
              );
            }

            case 'yourDeals':
              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-flyout-section-your-deals`}
                  renderContext="category_filter_section_your_deals"
                >
                  <FilterSectionYourDeals
                    activeFilters={activeFilters}
                    data={filter.type.yourDeals}
                    resetFilter={resetFilter}
                    resetFilters={resetFilters}
                    setFilter={onSetFilter}
                    setFilters={setFilters}
                    hide={hideFilterFlyout}
                    isOpen={openedFlyoutSlug === 'yourDeals'}
                    open={openFilterFlyout('yourDeals')}
                    submit={submitFilterFlyout}
                    enableFlyoutFilter={true}
                  />
                </ErrorBoundary>
              );

            case 'groupedAttributes': {
              const groupedAttributesData = filter.type.groupedAttributes;
              const filterId = groupedAttributesData.slug;

              return (
                <ErrorBoundary
                  ErrorComponent={ErrorComponent}
                  key={`filter-dropdown-section-grouped-attributes-${filterId}`}
                  renderContext={`category_filter_section_grouped_attributes-${filterId}`}
                >
                  <FilterSectionGroupedAttributes
                    activeFilters={activeFilters}
                    data={groupedAttributesData}
                    hide={hideFilterFlyout}
                    isOpen={openedFlyoutSlug === groupedAttributesData.slug}
                    open={openFilterFlyout(groupedAttributesData.slug)}
                    setFilter={onSetFilter}
                    submit={submitFilterFlyout}
                    enableFlyoutFilter={true}
                  />
                </ErrorBoundary>
              );
            }

            default:
              return null;
          }
        })}
      </FilterFlyoutSections>
      <FilterFlyoutFooter
        primaryButtonLabel={submitButtonLabel || defaultSubmitButtonLabel}
        primaryButtonDataTestId="filterFlyoutFooterSubmit"
        secondaryButtonLabel={t('Cancel')}
        secondaryButtonDataTestId="filterFlyoutFooterCancel"
        onClose={onClose}
        onPrimaryButtonClick={handlePrimaryButtonClick}
        hidePrimaryButton={
          !appliedFiltersCount &&
          !previousAppliedFiltersCount &&
          !sorting &&
          appliedFiltersCount === appliedFilterCountOnMount
        }
      />
    </>
  );
};

const ErrorComponent = () => null;
